import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
import { ChevronLeft, ChevronRight } from 'lucide-react';
/**
 * DoubleNumberLineRenderer — Interactive dubbele getallenlijn.
 *
 * The student sees two parallel horizontal number lines that share the same
 * coordinate space (left edge = 0% on both, right edge = max on both). Known
 * anchor points are pinned on BOTH lines and visually connected by a dashed
 * vertical line — this is the schoolbook "Wereld in Getallen" model for
 * proportional reasoning across procenten and verhoudingen.
 *
 * The student drags a marker along the "ask" line; the position is projected
 * to the OTHER line via a dashed projection so that the proportional link is
 * always visible. Answer payload is the numeric value of the marker on the
 * asked scale.
 *
 * Didactic anchor:
 *  - WiG / Pluspunt: dubbele getallenlijn voor procenten van een geheel
 *  - Tier-band: 7–14 (group 6 plus tot eind group 8)
 *  - DOK: 2 (strategische toepassing van verhoudings-redeneren)
 *
 * Validator: numeric tolerance, default 5% of askFor scale range.
 */
export const DoubleNumberLineRenderer: React.FC<QuestionRendererProps> = ({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}) => {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.doubleNumberLine;
  const hasConfig = !!config && !!config.topScale && !!config.bottomScale && Array.isArray(config.knownPoints) && (config.askFor === 'top' || config.askFor === 'bottom') && typeof config.correctValue === 'number';
  // ── Hooks must be unconditional ──────────────────────────────────────────
  const svgRef = useRef<SVGSVGElement>(null);
  const [position, setPosition] = useState<number | null>(null); // 0..1 along the shared axis
  const [dragging, setDragging] = useState(false);
  // Layout constants (viewBox space)
  const VB_WIDTH = 600;
  const VB_HEIGHT = 220;
  const PADDING_X = 50;
  const TOP_Y = 70;
  const BOTTOM_Y = 160;
  const AXIS_LEFT = PADDING_X;
  const AXIS_RIGHT = VB_WIDTH - PADDING_X;
  const AXIS_LENGTH = AXIS_RIGHT - AXIS_LEFT;
  // Reset on question change
  useEffect(() => {
    setPosition(null);
    setDragging(false);
  }, [question.id]);
  // Ready state
  useEffect(() => {
    onReadyChange?.(position !== null);
  }, [position, onReadyChange]);
  // ── Derived scales ───────────────────────────────────────────────────────
  const topScale = config?.topScale;
  const bottomScale = config?.bottomScale;
  const askFor = config?.askFor ?? 'bottom';
  const askedScale = askFor === 'top' ? topScale : bottomScale;
  const otherScale = askFor === 'top' ? bottomScale : topScale;
  const valueAtFraction = useCallback((frac: number, scale: {
    min: number;
    max: number;
  } | undefined) => {
    if (!scale) return 0;
    return scale.min + frac * (scale.max - scale.min);
  }, []);
  const fractionAtValue = useCallback((value: number, scale: {
    min: number;
    max: number;
  } | undefined) => {
    if (!scale || scale.max === scale.min) return 0;
    const f = (value - scale.min) / (scale.max - scale.min);
    return Math.max(0, Math.min(1, f));
  }, []);
  const currentValue = useMemo(() => {
    if (position === null || !askedScale) return null;
    return valueAtFraction(position, askedScale);
  }, [position, askedScale, valueAtFraction]);
  // NOTE: `currentOtherValue` was previously rendered as live text inside the SVG
  // and leaked the answer. Removed — only the visual projection marker remains.
  // ── Drag handling ────────────────────────────────────────────────────────
  const fractionFromClientX = useCallback((clientX: number): number => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const vbX = ratio * VB_WIDTH;
    const f = (vbX - AXIS_LEFT) / AXIS_LENGTH;
    return Math.max(0, Math.min(1, f));
  }, []);
  const handlePointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    hapticManager.hapticLight();
    setPosition(fractionFromClientX(e.clientX));
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    setPosition(fractionFromClientX(e.clientX));
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (dragging) {
      hapticManager.hapticMedium();
    }
    setDragging(false);
  };
  const handleTrackClick = (e: React.MouseEvent) => {
    hapticManager.hapticMedium();
    setPosition(fractionFromClientX(e.clientX));
  };
  const handleNudge = (direction: -1 | 1) => {
    if (shouldValidate) return;
    hapticManager.hapticLight();
    setPosition(prev => {
      const current = prev ?? 0.5;
      // Nudge by 1% of the scale
      const next = Math.max(0, Math.min(1, current + direction * 0.01));
      return next;
    });
  };
  // ── Edge-triggered submit ────────────────────────────────────────────────
  const answerPayload = useMemo(() => {
    if (currentValue === null) return 0;
    return Number(currentValue.toFixed(4));
  }, [currentValue]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // ── Early bail for malformed config (after all hooks) ────────────────────
  if (!hasConfig || !topScale || !bottomScale || !askedScale || !otherScale) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-12012" data-mp="1cyg31qz64w6">
        <p className="text-amber-700 font-bold" data-id="element-12013" data-mp="3sji119qizb87">
          ⚠️ Configuratie ontbreekt (doubleNumberLine)
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-12014" data-mp="4f36nuqjrbcl">
          Deze vraag mist <code data-id="element-12015" data-mp="4gw90xu4n9es">data.doubleNumberLine</code> met
          <code data-id="element-12016" data-mp="8fn5r3uokp1d"> topScale</code>, <code data-id="element-12017" data-mp="8etv3rslpfdy">bottomScale</code>,{' '}
          <code data-id="element-12018" data-mp="dfhw96ajnclw">knownPoints</code>, <code data-id="element-12019" data-mp="dezopdiflbyo">askFor</code> en{' '}
          <code data-id="element-12020" data-mp="dg12vququekm">correctValue</code>.
        </p>
      </div>;
  }
  // ── Format helpers ───────────────────────────────────────────────────────
  const formatValue = (v: number, scale: typeof topScale): string => {
    const abs = Math.abs(v);
    const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    const rounded = Number(v.toFixed(decimals));
    const num = rounded.toLocaleString('nl-NL');
    return scale?.unit ? `${num}${scale.unit}` : num;
  };
  // Tick generation: ~5 ticks across the scale
  const TICK_COUNT = 5;
  const ticks = Array.from({
    length: TICK_COUNT + 1
  }, (_, i) => i / TICK_COUNT);
  const promptLabel = config!.promptLabel ?? (askFor === 'top' ? `Welk ${topScale.label ?? 'getal'} hoort hierbij?` : `Welk ${bottomScale.label ?? 'getal'} hoort hierbij?`);
  const markerX = position !== null ? AXIS_LEFT + position * AXIS_LENGTH : null;
  const isReady = position !== null;
  return <div className="w-full max-w-2xl mx-auto space-y-5" data-id="element-12021" data-mp="2qmzfw3f202bh">
      {/* SVG — the double number line */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-slate-200 shadow-sm" data-id="element-12022" data-mp="2lp7m13uum64n">
        <svg ref={svgRef} viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`} className="w-full h-auto select-none touch-none" style={{
        maxHeight: 280
      }} role="img" aria-label={`Dubbele getallenlijn. ${topScale.label ?? 'Bovenste schaal'} van ${formatValue(topScale.min, topScale)} tot ${formatValue(topScale.max, topScale)}. ${bottomScale.label ?? 'Onderste schaal'} van ${formatValue(bottomScale.min, bottomScale)} tot ${formatValue(bottomScale.max, bottomScale)}.`} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} data-id="element-12023" data-mp="2gns1re0hw3nb">
          
          {/* Scale labels (left side) */}
          {topScale.label && <text x={AXIS_LEFT - 8} y={TOP_Y + 4} textAnchor="end" fontSize="12" fontWeight="700" fill="#64748b" data-id="element-12024" data-mp="3cuv1khl6ewh7">
            
              {topScale.label}
            </text>}
          {bottomScale.label && <text x={AXIS_LEFT - 8} y={BOTTOM_Y + 4} textAnchor="end" fontSize="12" fontWeight="700" fill="#64748b" data-id="element-12025" data-mp="32ukn6tnenj0g">
            
              {bottomScale.label}
            </text>}

          {/* Top axis */}
          <line x1={AXIS_LEFT} y1={TOP_Y} x2={AXIS_RIGHT} y2={TOP_Y} stroke={askFor === 'top' ? color : '#94a3b8'} strokeWidth={askFor === 'top' ? 3 : 2} strokeLinecap="round" data-id="element-12026" data-mp="2u1hgd479m4es" />
          
          {/* Bottom axis */}
          <line x1={AXIS_LEFT} y1={BOTTOM_Y} x2={AXIS_RIGHT} y2={BOTTOM_Y} stroke={askFor === 'bottom' ? color : '#94a3b8'} strokeWidth={askFor === 'bottom' ? 3 : 2} strokeLinecap="round" data-id="element-12027" data-mp="2k2rwj5vt69vw" />
          

          {/* Ticks + tick labels (endpoints + midpoint).
             Midpoint label is rendered as a faint reference so the student
             still has a proportional anchor without seeing live drag values. */}
          {ticks.map((t, i) => {
          const x = AXIS_LEFT + t * AXIS_LENGTH;
          const isEndpoint = i === 0 || i === TICK_COUNT;
          const isMidpoint = i === Math.floor(TICK_COUNT / 2);
          const topVal = valueAtFraction(t, topScale);
          const botVal = valueAtFraction(t, bottomScale);
          const showLabel = isEndpoint || isMidpoint;
          const labelOpacity = isEndpoint ? 1 : 0.55;
          return <g key={i} data-id="element-12028" data-mp="2ai20sf17kvmz" data-mpi={i}>
                <line x1={x} y1={TOP_Y - 6} x2={x} y2={TOP_Y + 6} stroke="#cbd5e1" strokeWidth={1.5} data-id="element-12029" data-mp="2agqy6iji1txg" />
                
                <line x1={x} y1={BOTTOM_Y - 6} x2={x} y2={BOTTOM_Y + 6} stroke="#cbd5e1" strokeWidth={1.5} data-id="element-12030" data-mp="2oarnymsh46lo" />
                
                {showLabel && <>
                    <text x={x} y={TOP_Y - 14} textAnchor="middle" fontSize="11" fontWeight={isEndpoint ? 600 : 500} fill="#64748b" opacity={labelOpacity} data-id="element-12031" data-mp="2y7wcuudpeg66">
                    
                      {formatValue(topVal, topScale)}
                    </text>
                    <text x={x} y={BOTTOM_Y + 22} textAnchor="middle" fontSize="11" fontWeight={isEndpoint ? 600 : 500} fill="#64748b" opacity={labelOpacity} data-id="element-12032" data-mp="12apz7jgiub5g">
                    
                      {formatValue(botVal, bottomScale)}
                    </text>
                  </>}
              </g>;
        })}

          {/* Known anchor points — dot on each line + dashed connector */}
          {config!.knownPoints.map((kp, idx) => {
          const fTop = fractionAtValue(kp.top, topScale);
          const fBot = fractionAtValue(kp.bottom, bottomScale);
          // Use the average fraction for positioning (they should align)
          const f = (fTop + fBot) / 2;
          const x = AXIS_LEFT + f * AXIS_LENGTH;
          return <g key={`kp-${idx}`} data-id="element-12033" data-mp="5kpblnrw20pq" data-mpi={idx}>
                <line x1={x} y1={TOP_Y} x2={x} y2={BOTTOM_Y} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="3 3" data-id="element-12034" data-mp="5lqo7wojyv9k" />
                
                <circle cx={x} cy={TOP_Y} r={6} fill="#475569" data-id="element-12035" data-mp="3mn1y41kxuos1" />
                <circle cx={x} cy={BOTTOM_Y} r={6} fill="#475569" data-id="element-12036" data-mp="3moms9q3cc5pd" />
                <text x={x} y={TOP_Y - 14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b" data-id="element-12037" data-mp="3rmy0xxhwga60">
                  
                  {formatValue(kp.top, topScale)}
                </text>
                <text x={x} y={BOTTOM_Y + 22} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b" data-id="element-12038" data-mp="57d9xrw9g0fp">
                  
                  {formatValue(kp.bottom, bottomScale)}
                </text>
              </g>;
        })}

          {/* Invisible hit-area track for clicking anywhere on the band */}
          <rect x={AXIS_LEFT} y={TOP_Y - 20} width={AXIS_LENGTH} height={BOTTOM_Y - TOP_Y + 40} fill="transparent" onClick={handleTrackClick} style={{
          cursor: 'pointer'
        }} data-id="element-12039" data-mp="j6mf7wixha4w" />
          

          {/* Active marker + projection */}
          {markerX !== null && <g data-id="element-12040" data-mp="2pvrcthtd7867">
              {/* Projection line */}
              <line x1={markerX} y1={TOP_Y} x2={markerX} y2={BOTTOM_Y} stroke={color} strokeWidth={2} strokeDasharray="4 3" opacity={0.6} data-id="element-12041" data-mp="2pyduia9yz425" />
            

              {/* Projected (passive) marker on the OTHER line */}
              <motion.circle cx={markerX} cy={askFor === 'top' ? BOTTOM_Y : TOP_Y} r={7} fill="white" stroke={color} strokeWidth={2.5} animate={{
            scale: dragging ? 1.05 : 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} data-id="element-12042" data-mp="2rtrd98sbueu9" />
            

              {/* Active (draggable) marker on the ASK line */}
              <motion.circle cx={markerX} cy={askFor === 'top' ? TOP_Y : BOTTOM_Y} r={16} fill={color} stroke="white" strokeWidth={3} animate={{
            scale: dragging ? 1.15 : 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 18
          }} style={{
            filter: `drop-shadow(0 4px 8px ${hexToRgba(color, 0.4)})`,
            cursor: dragging ? 'grabbing' : 'grab'
          }} data-id="element-12043" data-mp="2vtrybc9xloaa" />
            

              {/* NOTE: live numeric value labels removed — they leaked the answer.
               The student now sees the marker position without the exact number,
               which forces them to reason about the proportional position
               relative to known anchors (the schoolbook intent of the model). */}
            </g>}
        </svg>
      </div>

      {/* Prompt / status readout — does NOT show the chosen numeric value
               (that would leak the answer). Shows a neutral progress indicator
               and prompts the student to drag / nudge. The submit payload still
               carries the numeric position to the validator. */}
      <div className="rounded-2xl p-4 sm:p-5 border-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{
      backgroundColor: hexToRgba(color, 0.07),
      borderColor: hexToRgba(color, 0.25)
    }} data-id="element-12044" data-mp="3ahtd556wk306">
        
        <div className="flex-1" data-id="element-12045" data-mp="31h3p4nnvvgx0">
          <p className="text-sm font-bold uppercase tracking-wide" style={{
          color
        }} data-id="element-12046" data-mp="7pninpfcpuv1">
            
            {promptLabel}
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-12047" data-mp="3pd4hpheny53">
            {isReady ? 'Verschuif tot het klopt, druk dan op Check.' : `Sleep de stip op de ${askFor === 'top' ? 'bovenste' : 'onderste'} lijn of gebruik de knoppen.`}
          </p>
        </div>
        <div className="flex items-center gap-3 self-center sm:self-auto" data-id="element-12048" data-mp="3l4jqklwqpe1l">
          <button onClick={() => handleNudge(-1)} disabled={shouldValidate || position === 0} className="w-12 h-12 rounded-xl bg-white border-2 flex items-center justify-center text-slate-600 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" style={{
          borderColor: hexToRgba(color, 0.25)
        }} aria-label="Schuif naar links" data-id="element-12049" data-mp="3l2gupl8woqoy">
            
            <ChevronLeft className="w-6 h-6" data-id="element-12050" data-mp="1aa5v54ie3nmz" />
          </button>
          {/* Neutral "chosen" indicator — a colored dot when ready, hollow when not.
            Intentionally does NOT show the numeric value to avoid leaking the answer. */}
          <div className="px-4 py-2 rounded-xl bg-white border-2 min-w-[110px] text-center h-12 flex items-center justify-center gap-2" style={{
          borderColor: hexToRgba(color, 0.25)
        }} aria-label={isReady ? 'Positie gekozen' : 'Nog geen keuze'} data-id="element-12051" data-mp="1hv9j7ubkqi7o">
            
            <span className="rounded-full" style={{
            width: 12,
            height: 12,
            backgroundColor: isReady ? color : 'transparent',
            border: isReady ? 'none' : `2px solid ${hexToRgba(color, 0.4)}`
          }} data-id="element-12052" data-mp="tyc55vugu0hb" />
            
            <span className="text-sm font-bold" style={{
            color: isReady ? color : '#94a3b8'
          }} data-id="element-12053" data-mp="13ycqtoft4soo">
              
              {isReady ? 'gekozen' : 'kies een plek'}
            </span>
          </div>
          <button onClick={() => handleNudge(1)} disabled={shouldValidate || position === 1} className="w-12 h-12 rounded-xl bg-white border-2 flex items-center justify-center text-slate-600 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" style={{
          borderColor: hexToRgba(color, 0.25)
        }} aria-label="Schuif naar rechts" data-id="element-12054" data-mp="2jd0etpbo94bn">
            
            <ChevronRight className="w-6 h-6" data-id="element-12055" data-mp="oa3suprcgewv" />
          </button>
        </div>
      </div>
    </div>;
};